import { KeyValuePair, StorageObj } from 'src/interfaces/utility-interfaces';

import { AppConstants } from 'src/constants/constants';
import { DefaultHomePageCode } from 'src/constants/default-home-page.enum';
import { Injectable } from '@angular/core';
import { MyAccountService } from './my-account.service';
import { StorageService } from './storage.service';
import { Subject } from 'rxjs/internal/Subject';
import { UtilService } from './util.service';
import { fhirR4 } from '@smile-cdr/fhirts';

@Injectable({
  providedIn: 'root'
})
export class UserPreferenceService {

  userPreferenceSettings: Array<KeyValuePair> = [];
  currentDefaultHomePageCode: string;
  defaultHomePageChanged: Subject<string> = new Subject();

  constructor(
    private myAccountService: MyAccountService,
    private utilService: UtilService,
    private storageService: StorageService) {
      this.storageService.storageChange.subscribe((change: StorageObj) => {
        // Only care about user profile value update
        if (change.key === StorageService.keyUserProfileId) {
          this.getUserPreferences();
        }
      });
    }

  parseDefaultHomePageCode(userPreference: KeyValuePair[]) {
    if (userPreference && userPreference.length > 0) {
      const pageCode = userPreference.find(x => x.key === AppConstants.defaultHomePageLinkId)?.value;
      if (pageCode) {
        return pageCode;
      }
      return DefaultHomePageCode.PatientSearch;
    }
  }

  getUserPreferences() {
    return new Promise<Array<KeyValuePair>>((res, rej) => {
      const userProfileId = sessionStorage.getItem('userProfileID');
      this.myAccountService.getUserPreferencesQuestionnaire().then(async (data: fhirR4.Bundle) => {
        if (data.entry) {
          let newValue = '';
          if (data.entry.length === 1) {
            this.userPreferenceSettings = await this.fetchSettingsForProfile(userProfileId);
            if (this.userPreferenceSettings && this.userPreferenceSettings.length > 0) {
              newValue = this.parseDefaultHomePageCode(this.userPreferenceSettings);
            }
            if (this.currentDefaultHomePageCode !== newValue) {
              this.currentDefaultHomePageCode = newValue;
              this.defaultHomePageChanged.next(this.currentDefaultHomePageCode);
            }
            res(this.userPreferenceSettings);
          } else {
            throw new Error('There are ' + data.entry.length + ' responses for User Preferences Questionnaire');
          }
        } else {
          // Even backend has no record, the FHIR call would return an empty object, this exception should never happen
          throw new Error('There are no responses for User Preferences Questionnaire');
        }
      });
    });
  }

  private fetchSettingsForProfile(userProfileId: string) {
    // Fetch the QuestionnaireResponse related to the user profile
    return new Promise<Array<KeyValuePair>>((res, rej) => {
      this.myAccountService.getUserPreferencesQuestionnaireResponseForProfile(userProfileId)
        .then((data: fhirR4.Bundle) => {
          if (data.entry) {
            if (data.entry.length > 1) {
              this.utilService.sortList(data.entry, 'id');
              //console.log ('Warning: There are ' + data.entry.length + ' number of User Preferences for this profile');
            }
            const questionnaireResponse = data.entry[0].resource as fhirR4.QuestionnaireResponse;
            res(this.buildDefaultValues(questionnaireResponse));
          }
          res([]);
        }).catch(err => {
          console.warn(err);
        });
    });
  }

  private buildDefaultValues(questionnaireResponse: fhirR4.QuestionnaireResponse) {
    const userPreferences: Array<KeyValuePair> = [];
    if (questionnaireResponse.item) {
      questionnaireResponse.item.forEach((item: fhirR4.QuestionnaireResponseItem) => {
        const preference: KeyValuePair = {key : item.linkId, value : this.utilService.processAnswer(item.answer)};
        userPreferences.push(preference);
      });
    }
    return userPreferences;
  }
}
